/**
 * Timezone conversion utilities for subscription scheduling
 * 
 * Time zone handling:
 * - API stores times in UTC (for general timestamps) or US Eastern (for subscription schedules)
 * - UI displays times in US Central for user convenience
 * - Subscription create/edit converts Central → Eastern for API submission
 */

// Time zone constants
export const TIMEZONE_UTC = 'UTC';
export const TIMEZONE_US_CENTRAL = 'America/Chicago';
export const TIMEZONE_US_EASTERN = 'America/New_York';

/**
 * Convert UTC timestamp to US Central time for display
 * 
 * @param utcTimestamp - ISO string or Date object in UTC. Examples:
 *   - "2025-11-25T21:14:55" (assumes UTC)
 *   - "2025-11-25T21:14:55Z" (explicit UTC)
 *   - "2025-11-25T21:14:55+00:00" (UTC with offset)
 *   - Date object
 * @param formatType - Optional format type:
 *   - 'full': "Nov 25, 2025, 3:14 PM" 
 *   - 'short': "11/25/2025, 3:14 PM"
 *   - 'time': "3:14 PM"
 * @returns Formatted string in Central time, or empty string if invalid
 * 
 * @example
 * ```typescript
 * utcToCentralDisplay("2025-11-25T21:14:55") // "Nov 25, 2025, 3:14 PM"
 * utcToCentralDisplay("2025-11-25T21:14:55", 'time') // "3:14 PM"
 * ```
 */
export function utcToCentralDisplay(
  utcTimestamp: string | Date, 
  formatType: 'full' | 'short' | 'time' = 'full'
): string {
  if (!utcTimestamp) return '';
  
  try {
    let date: Date;
    
    if (typeof utcTimestamp === 'string') {
      // Check for actual timezone indicators at the end of the string
      const hasTimezoneInfo = utcTimestamp.endsWith('Z') || 
                              utcTimestamp.includes('+', utcTimestamp.indexOf('T')) || 
                              utcTimestamp.includes('-', utcTimestamp.indexOf('T'));
      
      if (hasTimezoneInfo) {
        // Already has timezone info - use direct parsing
        date = new Date(utcTimestamp);
      } else {
        // No timezone info - assume UTC and parse manually for reliable interpretation
        const match = utcTimestamp.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
        if (match) {
          const [, year, month, day, hour, minute, second] = match;
          date = new Date(Date.UTC(
            parseInt(year, 10),
            parseInt(month, 10) - 1, // Month is 0-based
            parseInt(day, 10),
            parseInt(hour, 10),
            parseInt(minute, 10),
            parseInt(second, 10)
          ));
        } else {
          // Fallback: try with Z suffix
          date = new Date(utcTimestamp + 'Z');
        }
      }
    } else {
      date = utcTimestamp;
    }
    
    if (isNaN(date.getTime())) {
      console.error('Invalid date provided to utcToCentralDisplay:', utcTimestamp);
      return '';
    }
    
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: TIMEZONE_US_CENTRAL,
      ...(formatType === 'full' && {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }),
      ...(formatType === 'short' && {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }),
      ...(formatType === 'time' && {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    });
    
    return formatter.format(date);
  } catch (error) {
    console.error('Error converting UTC to Central:', error);
    return '';
  }
}

/**
 * Convert US Eastern time to US Central time for display
 * @param easternTimestamp ISO string or Date object in Eastern time
 * @param formatType Optional format type ('full' | 'short' | 'time')
 * @returns Formatted string in Central time
 */
export function easternToCentralDisplay(
  easternTimestamp: string | Date,
  formatType: 'full' | 'short' | 'time' = 'full'
): string {
  if (!easternTimestamp) return '';
  
  try {
    // Parse the date assuming it's in Eastern time
    const date = typeof easternTimestamp === 'string' ? new Date(easternTimestamp) : easternTimestamp;
    
    if (isNaN(date.getTime())) {
      console.error('Invalid date provided to easternToCentralDisplay:', easternTimestamp);
      return '';
    }
    
    // Get the time difference between Eastern and Central (typically 1 hour)
    const easternOffset = getTimezoneOffset(date, TIMEZONE_US_EASTERN);
    const centralOffset = getTimezoneOffset(date, TIMEZONE_US_CENTRAL);
    const offsetDiff = easternOffset - centralOffset;
    
    // Adjust the date to Central time
    const centralDate = new Date(date.getTime() + (offsetDiff * 60 * 1000));
    
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: TIMEZONE_US_CENTRAL,
      ...(formatType === 'full' && {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }),
      ...(formatType === 'short' && {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }),
      ...(formatType === 'time' && {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    });
    
    return formatter.format(centralDate);
  } catch (error) {
    console.error('Error converting Eastern to Central:', error);
    return '';
  }
}

/**
 * Helper function to get timezone offset in minutes
 */
function getTimezoneOffset(date: Date, timezone: string): number {
  const utc1 = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const utc2 = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
  return (utc2.getTime() - utc1.getTime()) / (1000 * 60);
}

/**
 * Convert US Central time input to US Eastern for API submission
 * @param centralTime Date object or time string in Central time
 * @returns ISO string in Eastern time for API
 */
export function centralToEasternForApi(centralTime: Date | string): string {
  if (!centralTime) return '';
  
  try {
    let date: Date;
    
    if (typeof centralTime === 'string') {
      // If it's a time string like "14:30", combine with today's date
      const today = new Date();
      const [hours, minutes] = centralTime.split(':').map(Number);
      date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
    } else {
      date = centralTime;
    }
    
    // Add 1 hour to convert Central to Eastern (typical difference)
    const easternDate = new Date(date.getTime() + (60 * 60 * 1000));
    
    return easternDate.toISOString();
  } catch (error) {
    console.error('Error converting Central to Eastern:', error);
    return '';
  }
}

/**
 * Convert US Eastern time from API to US Central for form display
 * @param easternTime ISO string or Date object in Eastern time
 * @returns Date object in Central time for form controls
 */
export function easternToCentralForForm(easternTime: string | Date): Date | null {
  if (!easternTime) return null;
  
  try {
    const date = typeof easternTime === 'string' ? new Date(easternTime) : easternTime;
    
    if (isNaN(date.getTime())) {
      return null;
    }
    
    // Subtract 1 hour to convert Eastern to Central (typical difference)
    const centralDate = new Date(date.getTime() - (60 * 60 * 1000));
    
    return centralDate;
  } catch (error) {
    console.error('Error converting Eastern to Central for form:', error);
    return null;
  }
}

/**
 * Format time portion only for display (HH:mm format)
 * @param date Date object
 * @returns Time string in HH:mm format
 */
export function formatTimeOnly(date: Date | null): string {
  if (!date) return '';
  
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(date);
    const hour = parts.find(p => p.type === 'hour')?.value || '00';
    const minute = parts.find(p => p.type === 'minute')?.value || '00';
    
    return `${hour}:${minute}`;
  } catch (error) {
    console.error('Error formatting time:', error);
    return '';
  }
}

/**
 * Get current time in US Central for default form values
 * @returns Date object in Central time
 */
export function getCurrentCentralTime(): Date {
  const now = new Date();
  // Convert current time to Central timezone
  const centralTime = new Date(now.toLocaleString('en-US', { timeZone: TIMEZONE_US_CENTRAL }));
  return centralTime;
}

/**
 * Validate if a timestamp string is valid
 * @param timestamp ISO string
 * @returns boolean
 */
export function isValidTimestamp(timestamp: string): boolean {
  if (!timestamp) return false;
  
  try {
    const date = new Date(timestamp);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
}

/**
 * Convert time string (HH:mm) in Central to Eastern time string (HH:mm)
 * Used for schedule time conversion in forms
 * 
 * @param centralTimeString - Time string in HH:mm format (Central time)
 * @returns Time string in HH:mm format (Eastern time)
 * 
 * @example
 * ```typescript
 * convertScheduleTimeCentralToEastern("14:30") // "15:30"
 * convertScheduleTimeCentralToEastern("23:30") // "00:30" (next day)
 * ```
 */
export function convertScheduleTimeCentralToEastern(centralTimeString: string): string {
  if (!centralTimeString) return '';
  
  try {
    const [hours, minutes] = centralTimeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return centralTimeString;
    
    // Add 1 hour to convert Central to Eastern
    let easternHours = hours + 1;
    let easternMinutes = minutes;
    
    // Handle day rollover
    if (easternHours >= 24) {
      easternHours = easternHours - 24;
    }
    
    return `${easternHours.toString().padStart(2, '0')}:${easternMinutes.toString().padStart(2, '0')}`;
  } catch (error) {
    console.error('Error converting schedule time Central to Eastern:', error);
    return centralTimeString; // Return original if conversion fails
  }
}

/**
 * Convert time string (HH:mm) in Eastern to Central time string (HH:mm)
 * Used for schedule time conversion when editing existing subscriptions
 * 
 * @param easternTimeString - Time string in HH:mm format (Eastern time)
 * @returns Time string in HH:mm format (Central time)
 * 
 * @example
 * ```typescript
 * convertScheduleTimeEasternToCentral("15:30") // "14:30"
 * convertScheduleTimeEasternToCentral("00:30") // "23:30" (previous day)
 * ```
 */
export function convertScheduleTimeEasternToCentral(easternTimeString: string): string {
  if (!easternTimeString) return '';
  
  try {
    const [hours, minutes] = easternTimeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return easternTimeString;
    
    // Subtract 1 hour to convert Eastern to Central
    let centralHours = hours - 1;
    let centralMinutes = minutes;
    
    // Handle day rollover backwards
    if (centralHours < 0) {
      centralHours = 24 + centralHours;
    }
    
    return `${centralHours.toString().padStart(2, '0')}:${centralMinutes.toString().padStart(2, '0')}`;
  } catch (error) {
    console.error('Error converting schedule time Eastern to Central:', error);
    return easternTimeString; // Return original if conversion fails
  }
}